import {
  Component,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  ICreateListadoCategoria,
  IListadoCategoria,
  ITipoListadoCategoria,
} from 'modelos/src';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { FormsModule } from '@angular/forms';
import { ListadoCategoriasSelectorCacheService } from './listado-categoria-selector-cache.service';
import { ListadoCategoriasService } from '../../../auxiliares/servicios/http/listado-categorias.service';
import { ButtonComponent } from '../../../auxiliares/button/button.component';
import { TooltipDirective } from '../../../auxiliares/tooltip/tooltip.directive';
import { MatIconModule } from '@angular/material/icon';
import { BaseSelectorComponent } from '../base-selector';
import { formatearCategoria } from '../../../modulos/standalone/listado-categorias/categoria-display';
import { FormularioDialogService } from '../../../auxiliares/servicios/formulario-dialog.service';

@Component({
  selector: 'app-listado-categoria-selector',
  templateUrl: './listado-categoria-selector.component.html',
  styleUrls: ['./listado-categoria-selector.component.scss'],
  standalone: true,
  imports: [FormsModule, NgSelectModule, ButtonComponent, MatTooltipModule, MatIconModule, TooltipDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ListadoCategoriasSelectorComponent),
      multi: true,
    },
  ],
})
export class ListadoCategoriasSelectorComponent
  extends BaseSelectorComponent<IListadoCategoria>
  implements OnInit, OnChanges
{
  private cacheService = inject(ListadoCategoriasSelectorCacheService);
  private listadoCategoriasService = inject(ListadoCategoriasService);
  private formularios = inject(FormularioDialogService);

  @Input() override placeholder = 'Buscar y seleccionar categoría';
  @Input() override notFoundText = 'No se encontraron categorías';
  /** Cliente para filtrar los resultados */
  @Input() idCliente: string | null = null;
  /** Categoría para filtrar los resultados (ej: 'Sintomas', 'Medicamentos') */
  @Input() categoria: string | null = null;
  /** Array de categorías que se pueden crear (usado en el dialog y tooltip) */
  @Input() categorias: string[] | null = null;
  /** Tooltip personalizado para el botón de crear */
  @Input() tooltipCrear: string | null = null;
  /** Título personalizado para el diálogo de crear */
  @Input() tituloCrear: string | null = null;
  /**
   * Nombres iniciales para pre-seleccionar. Útil al editar: se pasan los
   * nombres guardados y el selector los resuelve a objetos internamente.
   * Soporta nombres exactos y nombres con complemento (ej: "Dolor de cabeza severo"
   * encuentra el ítem "Dolor de cabeza...").
   */
  @Input() initialNames: string[] = [];

  @Input() allowCreate = false;

  @Output() listadoCategoriaChange =
    new EventEmitter<IListadoCategoria | null>();
  @Output() listadoCategoriaIdChange = new EventEmitter<string | null>();
  @Output() listadoCategoriasChange = new EventEmitter<IListadoCategoria[]>();
  @Output() listadoCategoriasIdsChange = new EventEmitter<string[]>();

  public listadoCategorias: IListadoCategoria[] = [];
  public creandoTag = false;

  protected get listaItems(): IListadoCategoria[] {
    return this.listadoCategorias;
  }

  public formatearCategoria(cat: string): string {
    return formatearCategoria(cat);
  }

  public fomatearCategoriaMinuscula(cat: string): string {
    return formatearCategoria(cat).toLocaleLowerCase();
  }

  public get tooltipCrearCategoria(): string {
    if (this.tooltipCrear) return this.tooltipCrear;
    if (this.categorias?.length === 1) {
      return `Crear ${this.fomatearCategoriaMinuscula(this.categorias[0])}`;
    } else if (this.categorias?.length) {
      const categoriasFormateadas = this.categorias
        .map((c) => this.fomatearCategoriaMinuscula(c))
        .join(', ');
      return `Crear ${categoriasFormateadas}`;
    } else if (this.categoria) {
      return `Crear ${this.fomatearCategoriaMinuscula(this.categoria)}`;
    }
    return 'Crear nueva categoría';
  }

  public addTagFn = async (nombre: string): Promise<IListadoCategoria> => {
    this.creandoTag = true;
    try {
      const nuevo: ICreateListadoCategoria = {
        nombre,
        ...(this.categoria
          ? { categoria: this.categoria as ITipoListadoCategoria }
          : {}),
        ...(this.idCliente ? { idCliente: this.idCliente } : {}),
        global: false,
      };
      const creado = await this.listadoCategoriasService.create(nuevo);
      this.recargarCategorias().catch(console.error);
      return creado;
    } finally {
      this.creandoTag = false;
    }
  };

  // ── Eventos del ng-select ───────────────────────────────────────────────

  onListadoCategoriaChange(
    value: IListadoCategoria | IListadoCategoria[] | null,
  ): void {
    this.innerValue = value;
    this.emitChange(value);
    this.onTouched();

    if (this.multiple) {
      const arr = Array.isArray(value) ? value : [];
      this.listadoCategoriasChange.emit(arr);
      this.listadoCategoriasIdsChange.emit(arr.map((c) => c._id!));
    } else {
      const c = Array.isArray(value) ? null : value;
      this.listadoCategoriaChange.emit(c);
      this.listadoCategoriaIdChange.emit(c?._id ?? null);
    }
  }

  // ── Funciones para ng-select ────────────────────────────────────────────

  public searchFn = (term: string, item: IListadoCategoria): boolean => {
    return (item.nombre ?? '').toLowerCase().includes(term.toLowerCase());
  };

  // ── Crear categoría ─────────────────────────────────────────────────────

  public async abrirCrearCategoria(): Promise<void> {
    const { CrearEditarListadoCategoriaComponent } =
      await import('../../../modulos/standalone/listado-categorias/crear-editar-listado-categoria/crear-editar-listado-categoria.component');

    const dialogRef = this.formularios.abrir(
      CrearEditarListadoCategoriaComponent,
      {
        tamano: 'mediano',
        data: {
          idCliente: this.idCliente,
          categoria: this.categoria,
          categorias: this.categorias,
          titulo: this.tituloCrear,
        },
      },
    );

    dialogRef.afterClosed().subscribe((nuevoCategoria: IListadoCategoria) => {
      if (!nuevoCategoria) return;

      this.cacheService.invalidate(
        this.idCliente ?? undefined,
        this.categoria ?? undefined,
        this.categorias ?? undefined,
      );

      this.subscription?.unsubscribe();
      this.loading = true;
      this.subscription = this.cacheService
        .getListadoCategorias(
          this.idCliente ?? undefined,
          this.categoria ?? undefined,
          this.categorias ?? undefined,
        )
        .subscribe({
          next: (categorias) => {
            this.listadoCategorias = categorias;
            this.loading = false;
            const categoriaCreada = categorias.find(
              (c) => c._id === nuevoCategoria._id,
            );
            if (categoriaCreada) {
              if (this.multiple) {
                const current = Array.isArray(this.innerValue)
                  ? this.innerValue
                  : [];
                this.onListadoCategoriaChange([...current, categoriaCreada]);
              } else {
                this.onListadoCategoriaChange(categoriaCreada);
              }
            }
          },
          error: (err) => {
            console.error('Error recargando categorías:', err);
            this.loading = false;
          },
        });
    });
  }

  // ── Carga de datos ──────────────────────────────────────────────────────

  private recargarCategorias(): Promise<void> {
    this.cacheService.invalidate(
      this.idCliente ?? undefined,
      this.categoria ?? undefined,
      this.categorias ?? undefined,
    );
    return new Promise((resolve, reject) => {
      this.subscription?.unsubscribe();
      this.subscription = this.cacheService
        .getListadoCategorias(
          this.idCliente ?? undefined,
          this.categoria ?? undefined,
          this.categorias ?? undefined,
        )
        .subscribe({
          next: (categorias) => {
            this.listadoCategorias = categorias;
            resolve();
          },
          error: (err) => reject(err),
        });
    });
  }

  private cargarCategorias(): void {
    this.subscription?.unsubscribe();
    this.loading = true;

    this.subscription = this.cacheService
      .getListadoCategorias(
        this.idCliente ?? undefined,
        this.categoria ?? undefined,
        this.categorias ?? undefined,
      )
      .subscribe({
        next: (categorias) => {
          this.listadoCategorias = categorias;
          this.loading = false;
          this.resolvePendingId();
          if (this.initialNames.length > 0 && this.isValueEmpty()) {
            this.resolveByNames(this.initialNames);
          }
        },
        error: (err) => {
          console.error('Error cargando categorías:', err);
          this.loading = false;
        },
      });
  }

  /**
   * Resuelve nombres a objetos IListadoCategoria y los pre-selecciona.
   * Solo actúa si no hay ya una selección activa.
   * Soporta match exacto y match de nombre base (para ítems que terminan en "...").
   */
  private resolveByNames(names: string[]): void {
    const items = names
      .map((name) => {
        const exact = this.listadoCategorias.find((c) => c.nombre === name);
        if (exact) return exact;
        return (
          this.listadoCategorias.find((c) => {
            const base = (c.nombre ?? '').replace(/\.{3}$|…$/, '');
            return base && name.startsWith(base);
          }) ?? null
        );
      })
      .filter((c): c is IListadoCategoria => c !== null);

    if (!items.length) return;
    this.innerValue = this.multiple ? items : items[0];
  }

  private isValueEmpty(): boolean {
    if (this.multiple) {
      return (
        !this.innerValue ||
        (Array.isArray(this.innerValue) && this.innerValue.length === 0)
      );
    }
    return this.innerValue === null || this.innerValue === undefined;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.cargarCategorias();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const idClienteChanged =
      changes['idCliente'] && !changes['idCliente'].firstChange;
    const categoriaChanged =
      changes['categoria'] && !changes['categoria'].firstChange;

    if (idClienteChanged || categoriaChanged) {
      this.listadoCategorias = [];
      this.innerValue = this.multiple ? [] : null;
      this.cargarCategorias();
    }

    if (changes['initialNames'] && !changes['initialNames'].firstChange) {
      if (this.listadoCategorias.length > 0 && this.isValueEmpty()) {
        this.resolveByNames(this.initialNames);
      }
    }
  }
}
